import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Iconify from "../../layouts/components/iconify/iconify";
import Avatar from "../../components/ui/avatar/Avtar";
import CardSection from "../../components/ui/card/CardSection";
import DropdownSelect from "../../components/ui/dropdown/DropdownSelect";
import Buttons from "../../components/ui/button/Buttons";
import DataLoader from "../../components/ui/dataLoader/DataLoader";
import NoDataFound from "../../components/ui/noDataFound/NoDataFound";
import Tooltip from "../../components/ui/tooltip/Tooltip";
import { RenderChart } from "../../layouts/components/chart/RenderChart";
import { useGetTeamOverviewDashboardMutation } from "../../app/services/teamOverviewAPI";
import { decryptUrlData } from "../../services/CryptoService";
import {
  buildDashboardRequest,
  clampPercent,
  countUniqueProjects,
  formatDateRangeLabel,
  formatHours,
  formatPlanningOptionLabel,
  getCompletionProgressPercent,
  getDisplayUtilizationStatus,
  getUtilizationStatusClass,
  MEMBER_SORT_OPTIONS,
  padStatusBreakdown,
  sortMembers,
  toFilterOptions,
  transformChartData,
  transformStatusDistribution,
} from "./teamOverviewUtils";
import { useTeamOverviewDrilldown } from "./features/useTeamOverviewDrilldown";
import OverviewFilterField from "./features/OverviewFilterField";
import OverviewDateRangeField from "./features/OverviewDateRangeField";
import "./TeamOverview.scss";

const emptyFilters = {
  planning: null,
  project: null,
  member: null,
  taskCategory: null,
  fromDate: null,
  toDate: null,
};

const KPI_META = [
  { key: "totalTasks", label: "Total Tasks", icon: "mdi:file-document-outline" },
  { key: "completedTasks", label: "Completed", icon: "mdi:check-circle-outline" },
  { key: "activeTasks", label: "Active", icon: "mdi:play-circle-outline" },
  { key: "overdueTasks", label: "Overdue", icon: "mdi:alert-circle-outline" },
  { key: "unassignedTasks", label: "Unassigned", icon: "mdi:account-group-outline" },
  { key: "teamUtilization", label: "Team Utilization", icon: "mdi:percent-outline", format: "percent" },
  { key: "hours", label: "Logged / Estimated Hours", icon: "mdi:clock-outline", format: "hoursPair" },
];

const LIST_VIEW_MEDIA_QUERY = "(min-width: 1600px)";

// Temporarily disabled drill-downs. Set an item to true to restore its existing handler.
const OVERVIEW_NAVIGATION_ENABLED = {
  workloadMember: false,
  projectStatusChart: false,
  memberTaskSummary: false,
  expandedProjectStatus: false,
};

const TeamOverview = () => {
  const { id } = useParams();
  let teamData = null;
  try {
    teamData = id ? decryptUrlData(id) : null;
  } catch {
    teamData = null;
  }

  const teamId = teamData?.teamId;
  const teamName = teamData?.teamName || "Team";
  const { openTaskList } = useTeamOverviewDrilldown(id);

  const [filterOptions, setFilterOptions] = useState({
    planningCycles: [],
    projects: [],
    members: [],
    taskCategories: [],
  });
  const [filters, setFilters] = useState(emptyFilters);
  const [dashboard, setDashboard] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [defaultsReady, setDefaultsReady] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [memberSort, setMemberSort] = useState(MEMBER_SORT_OPTIONS[0]);
  const [memberView, setMemberView] = useState("grid");
  const [canUseListView, setCanUseListView] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(LIST_VIEW_MEDIA_QUERY).matches : false
  );
  const [openProjects, setOpenProjects] = useState({});
  const [openTaskInfo, setOpenTaskInfo] = useState({});

  const [getDashboard, { isLoading, isError }] = useGetTeamOverviewDashboardMutation();

  useEffect(() => {
    const mediaQuery = window.matchMedia(LIST_VIEW_MEDIA_QUERY);
    const handleScreenChange = (event) => {
      setCanUseListView(event.matches);
      if (!event.matches) {
        setMemberView("grid");
      }
    };

    setCanUseListView(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleScreenChange);

    return () => mediaQuery.removeEventListener("change", handleScreenChange);
  }, []);

  const applyDrillFilters = useCallback(
    (extra = {}) => {
      openTaskList({
        planningId: filters.planning?.value ?? null,
        projectId: filters.project?.value ?? null,
        userId: filters.member?.value ?? null,
        taskCategoryId: filters.taskCategory?.value ?? null,
        fromDate: filters.fromDate || null,
        toDate: filters.toDate || null,
        ...extra,
      });
    },
    [filters, openTaskList]
  );

  const loadDashboard = useCallback(
    async (nextFilters) => {
      if (!teamId) return;
      setLoadError(null);
      try {
        const data = await getDashboard(buildDashboardRequest(teamId, nextFilters)).unwrap();
        if (!data) {
          setLoadError("Unable to load Team Overview.");
          setDashboard(null);
          setHasLoaded(true);
          return;
        }
        setDashboard(data);
        const planningCycles = toFilterOptions(data.filters?.planningCycles).map((opt) => ({
          ...opt,
          cycleName: opt.label,
          label: formatPlanningOptionLabel(opt.label, opt.startDate, opt.endDate),
        }));
        setFilterOptions({
          planningCycles,
          projects: toFilterOptions(data.filters?.projects),
          members: toFilterOptions(data.filters?.members),
          taskCategories: toFilterOptions(data.filters?.taskCategories),
        });
        setFilters((prev) => {
          if (!prev.planning) return prev;
          const match = planningCycles.find((opt) => String(opt.value) === String(prev.planning.value));
          if (!match) return prev;
          return {
            ...prev,
            planning: {
              ...prev.planning,
              label: match.label,
              cycleName: match.cycleName,
              startDate: match.startDate,
              endDate: match.endDate,
            },
          };
        });
        setHasLoaded(true);
      } catch {
        setLoadError("Unable to load Team Overview.");
        setDashboard(null);
        setHasLoaded(true);
      }
    },
    [getDashboard, teamId]
  );

  const resolveDefaults = useCallback((planningCycles) => {
    const live = (planningCycles || []).find((p) => p.isLive);
    const planning = live
      ? {
        value: live.id,
        label: formatPlanningOptionLabel(live.name, live.startDate, live.endDate),
        cycleName: live.name,
        startDate: live.startDate,
        endDate: live.endDate,
        isLive: true,
      }
      : null;
    return {
      ...emptyFilters,
      planning,
      // Do not auto-apply deadline date filters with the live planning —
      // PlanningId alone scopes tasks; dates were zeroing Sprint results.
      fromDate: null,
      toDate: null,
    };
  }, []);

  useEffect(() => {
    if (!teamId || defaultsReady) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getDashboard({ teamId }).unwrap();
        if (cancelled) return;
        const cycles = data?.filters?.planningCycles || [];
        const defaults = resolveDefaults(cycles);
        setFilters(defaults);
        setDefaultsReady(true);
        await loadDashboard(defaults);
      } catch {
        if (!cancelled) {
          setDefaultsReady(true);
          setHasLoaded(true);
          setLoadError("Unable to load Team Overview.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "planning") {
        if (value) {
          // Keep date range empty when a planning is chosen so we don't double-filter.
          next.fromDate = null;
          next.toDate = null;
        }
      }
      return next;
    });
  };

  const handleApply = () => loadDashboard(filters);

  const handleReset = async () => {
    const defaults = resolveDefaults(
      (filterOptions.planningCycles || []).map((o) => ({
        id: o.value,
        name: o.cycleName || o.label,
        startDate: o.startDate,
        endDate: o.endDate,
        isLive: o.isLive,
      }))
    );
    setFilters(defaults);
    await loadDashboard(defaults);
  };

  const clearChip = async (key) => {
    const next = { ...filters };
    if (key === "planning") {
      next.planning = null;
    } else if (key === "project") {
      next.project = null;
    } else if (key === "member") {
      next.member = null;
    } else if (key === "taskCategory") {
      next.taskCategory = null;
    } else if (key === "dateRange") {
      next.fromDate = null;
      next.toDate = null;
    }
    setFilters(next);
    await loadDashboard(next);
  };

  const summary = dashboard?.summary || {};
  const workload = dashboard?.workload || [];
  const members = dashboard?.members || [];
  const projectStatusRows = dashboard?.projectStatusChart || [];
  const { chartData, taskColors } = transformChartData(projectStatusRows);
  const statusDistribution = transformStatusDistribution(projectStatusRows, summary);
  const sortedMembers = useMemo(
    () => sortMembers(members, memberSort?.value || "util-desc"),
    [members, memberSort]
  );

  const toggleMemberProject = (userId, projectId) => {
    const key = `${userId}-${projectId}`;
    setOpenProjects((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleTaskInfo = (userId) => {
    setOpenTaskInfo((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  const dateRangeLabel = formatDateRangeLabel(filters.fromDate, filters.toDate);
  const planningRangeLabel = formatDateRangeLabel(
    filters.planning?.startDate,
    filters.planning?.endDate
  );
  const projectCount = countUniqueProjects(projectStatusRows);
  const memberCount = workload.length || members.length;
  const headerMeta = [
    planningRangeLabel || dateRangeLabel,
    memberCount ? `${memberCount} Member${memberCount === 1 ? "" : "s"}` : null,
    projectCount ? `${projectCount} Project${projectCount === 1 ? "" : "s"}` : null,
  ]
    .filter(Boolean)
    .join(" | ");

  const activeChips = [];
  if (filters.planning) {
    activeChips.push({
      key: "planning",
      label: `Planning: ${filters.planning.label}`,
    });
  }
  if (filters.project) {
    activeChips.push({ key: "project", label: `Project: ${filters.project.label}` });
  }
  if (filters.member) {
    activeChips.push({ key: "member", label: `Member: ${filters.member.label}` });
  }
  if (filters.taskCategory) {
    activeChips.push({ key: "taskCategory", label: `Task Type: ${filters.taskCategory.label}` });
  }
  if ((filters.fromDate || filters.toDate) && !filters.planning) {
    activeChips.push({ key: "dateRange", label: `Date Range: ${dateRangeLabel}` });
  }

  const renderKpiValue = (meta) => {
    if (meta.format === "hoursPair") {
      return `${formatHours(summary.loggedHours)} / ${formatHours(summary.estimatedHours)}`;
    }
    const raw = summary[meta.key];
    if (meta.format === "percent") return `${Number(raw || 0).toFixed(1)}%`;
    return raw ?? 0;
  };

  const totalStatusTasks =
    statusDistribution.reduce((sum, item) => sum + (item.data || 0), 0) || summary.totalTasks || 0;

  const renderMemberCard = (user, view) => {
    const isList = view === "list";
    const displayStatus = getDisplayUtilizationStatus(
      user.utilizationStatus,
      user.plannedHours
    );
    const util = Number(user.utilization || 0);
    const statusBreakdown = padStatusBreakdown(user.statusBreakdown || []);
    const totalStatus = statusBreakdown.reduce(
      (sum, item) => sum + (item.taskCount || 0),
      0
    );
    const projects = user.projects || [];
    const capacityHours =
      user.availableHours != null && Number(user.availableHours) > 0
        ? user.availableHours
        : user.productiveHours;

    return (
      <div className={`member-card member-card--${view}`} key={user.userId}>
        <div className="member-card-top">
          <div className="member-card-identity">
            <Avatar alt={user.userName} size="medium" shape="circle" />
            <div className="member-card-name-wrap">
              <div className="member-card-name">{user.userName}</div>
              {isList ? (
                <span className={`util-badge ${getUtilizationStatusClass(displayStatus)}`}>
                  {displayStatus}
                </span>
              ) : null}
            </div>
          </div>

          <div className="member-card-badges">
            <span className="member-util-pill">{util.toFixed(0)}%</span>
            {isList ? (
              <div className="util-progress member-list-util-progress">
                <div
                  className="util-progress-fill"
                  style={{ width: `${clampPercent(util)}%` }}
                />
              </div>
            ) : (
              <span className={`util-badge ${getUtilizationStatusClass(displayStatus)}`}>
                {displayStatus}
              </span>
            )}
          </div>
        </div>

        {isList ? (
          <div className="member-card-util-block">
            <div className="member-card-util-label">Utilization</div>
            <div className="member-card-util-value">
              <span className="member-util-pill">{util.toFixed(0)}%</span>
              <div className="util-progress">
                <div
                  className="util-progress-fill"
                  style={{ width: `${clampPercent(util)}%` }}
                />
              </div>
            </div>
          </div>
        ) : null}

        <div className="member-card-capacity">
          <div className="member-card-capacity-row">
            {!isList ? <span>Planned / Capacity</span> : null}
            <span>
              {formatHours(user.plannedHours)} / {formatHours(capacityHours)}
            </span>
          </div>
          {!isList && Number(user.indirectHours || 0) > 0 ? (
            <div className="member-card-capacity-row member-card-capacity-row--muted">
              <span>Indirect</span>
              <span>{formatHours(user.indirectHours)}</span>
            </div>
          ) : null}
          <div className="util-progress">
            <div
              className="util-progress-fill"
              style={{ width: `${clampPercent(util)}%` }}
            />
          </div>
        </div>

        {isList ? (
          <div className="member-card-bifurcation">
            <div>
              <strong>{formatHours(user.productiveHours)}</strong>
              <span>Productive</span>
            </div>
            <div>
              <strong>{formatHours(user.indirectHours)}</strong>
              <span>Indirect</span>
            </div>
          </div>
        ) : null}

        <div className="member-card-status">
          {!isList ? <div className="member-card-status-title">Task Details</div> : null}
          <div className="member-status-grid">
            {statusBreakdown.map((status) => (
              <button
                type="button"
                className="member-status-item"
                key={`${user.userId}-${status.statusCategoryId}`}
                title={status.statusCategoryName}
                aria-label={`${status.statusCategoryName}: ${status.taskCount}`}
                disabled={!OVERVIEW_NAVIGATION_ENABLED.memberTaskSummary}
                onClick={
                  OVERVIEW_NAVIGATION_ENABLED.memberTaskSummary
                    ? () => applyDrillFilters({ userId: user.userId })
                    : undefined
                }
              >
                <div className="member-status-count">{status.taskCount}</div>
                <div
                  className="member-status-name"
                  style={{ color: status.color || "#64748B" }}
                >
                  <span
                    className="status-dot"
                    style={{ backgroundColor: status.color || "#8592A6" }}
                  />
                  <span className="member-status-label">{status.statusCategoryName}</span>
                </div>
              </button>
            ))}
          </div>
          {totalStatus > 0 ? (
            <div className="member-status-bar">
              {statusBreakdown.map((status, index) => {
                if (!status.taskCount) return null;
                const tipId = `tip-${user.userId}-${status.statusCategoryId}-${index}`;
                return (
                  <div
                    key={tipId}
                    id={tipId}
                    className="member-status-bar-segment"
                    style={{
                      width: `${(status.taskCount / totalStatus) * 100}%`,
                      backgroundColor: status.color || "#8592A6",
                    }}
                  >
                    <Tooltip
                      label={`${status.taskCount} Task${status.taskCount === 1 ? "" : "s"}`}
                      targetId={tipId}
                      position="top"
                      className="tooltip"
                    />
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>

        <div className="member-card-projects">
          {isList ? (
            <button
              type="button"
              className="member-card-section-toggle"
              onClick={() => toggleTaskInfo(user.userId)}
              aria-expanded={!!openTaskInfo[user.userId]}
            >
              <span className="member-card-status-title">Projects</span>
              <span className="member-card-section-meta">
                {projects.length > 0
                  ? `${projects.length} Project${projects.length === 1 ? "" : "s"}`
                  : "None"}
                <Iconify
                  icon={
                    openTaskInfo[user.userId] ? "mingcute:up-line" : "mingcute:down-line"
                  }
                />
              </span>
            </button>
          ) : (
            <div className="member-card-projects-heading">
              <span className="member-card-status-title">Projects</span>
              <span className="member-card-project-count">
                {projects.length > 0
                  ? `${projects.length} Project${projects.length === 1 ? "" : "s"}`
                  : "None"}
              </span>
            </div>
          )}
          {!isList || openTaskInfo[user.userId] ? (
            projects.length > 0 ? (
              <div className={`task-information-list task-information-list--${view}`}>
                {isList ? (
                  <div className="task-information-table-header">
                    <span>Project Name</span>
                    <span>Total Tasks</span>
                    <span>Planned Hours</span>
                    {padStatusBreakdown([]).map((status) => (
                      <span key={status.statusCategoryId}>{status.statusCategoryName}</span>
                    ))}
                    <span>Progress</span>
                    <span>Actions</span>
                  </div>
                ) : null}
                {projects.map((project) => {
                  const projectKey = `${user.userId}-${project.projectId}`;
                  const isOpen = !!openProjects[projectKey];
                  const projectStatuses = padStatusBreakdown(project.statusBreakdown || []);
                  const activeProjectStatuses = projectStatuses.filter(
                    (status) => Number(status.taskCount) > 0
                  );
                  const projectProgress = getCompletionProgressPercent(projectStatuses);
                  return (
                    <div
                      className={`task-information-item ${!isList && isOpen ? "is-open" : ""}`}
                      key={projectKey}
                    >
                      <button
                        type="button"
                        className="task-information-header"
                        aria-expanded={!isList ? isOpen : undefined}
                        onClick={() => {
                          if (!isList) {
                            toggleMemberProject(user.userId, project.projectId);
                          }
                        }}
                      >
                        <div className="task-information-left">
                          <Iconify
                            icon={
                              isList
                                ? "mdi:briefcase-outline"
                                : isOpen
                                  ? "mingcute:down-line"
                                  : "weui:arrow-filled"
                            }
                          />
                          <span className="task-information-name">{project.projectName}</span>
                          {!isList ? (
                            <span className="task-information-count">({project.taskCount})</span>
                          ) : null}
                        </div>
                        {isList ? (
                          <>
                            <span className="task-information-count">{project.taskCount}</span>
                            <span className="task-information-hours">
                              {formatHours(project.estimatedHours)}
                            </span>
                            {projectStatuses.map((status) => (
                              <span
                                className="task-information-status-summary"
                                key={`${projectKey}-summary-${status.statusCategoryId}`}
                              >
                                <span
                                  className="status-dot"
                                  style={{ backgroundColor: status.color }}
                                />
                                {status.taskCount}
                              </span>
                            ))}
                            <span className="task-information-progress">
                              <span>
                                <i style={{ width: `${clampPercent(projectProgress)}%` }} />
                              </span>
                              {Math.round(projectProgress)}%
                            </span>
                            <span
                              className="task-information-link"
                              onClick={(event) => {
                                event.stopPropagation();
                                applyDrillFilters({
                                  userId: user.userId,
                                  projectId: project.projectId,
                                });
                              }}
                            >
                              View Project Tasks
                              <Iconify icon="mdi:arrow-right" />
                            </span>
                          </>
                        ) : (
                          <div className="task-information-hours">
                            {formatHours(project.estimatedHours)}
                          </div>
                        )}
                      </button>

                      {!isList && isOpen ? (
                        <div className="task-information-body">
                          {activeProjectStatuses.length > 0 ? (
                            <div className="task-information-statuses">
                              {activeProjectStatuses.map((status) => (
                                <button
                                  type="button"
                                  className="task-information-status"
                                  key={`${projectKey}-${status.statusCategoryId}`}
                                  disabled={!OVERVIEW_NAVIGATION_ENABLED.expandedProjectStatus}
                                  onClick={
                                    OVERVIEW_NAVIGATION_ENABLED.expandedProjectStatus
                                      ? () =>
                                        applyDrillFilters({
                                          userId: user.userId,
                                          projectId: project.projectId,
                                        })
                                      : undefined
                                  }
                                >
                                  <span
                                    className="status-dot"
                                    style={{ backgroundColor: status.color }}
                                  />
                                  <span className="task-information-status-name">
                                    {status.statusCategoryName}
                                  </span>
                                  <span className="task-information-status-sep">:</span>
                                  <span className="task-information-status-count">
                                    {status.taskCount}
                                  </span>
                                </button>
                              ))}
                            </div>
                          ) : (
                            <div className="member-status-empty">No tasks</div>
                          )}
                          <button
                            type="button"
                            className="task-information-link"
                            onClick={() =>
                              applyDrillFilters({
                                userId: user.userId,
                                projectId: project.projectId,
                              })
                            }
                          >
                            <Iconify icon="carbon:view" />
                            View Project Tasks
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="member-status-empty">No project tasks</div>
            )
          ) : null}
        </div>

        <button
          type="button"
          className={`member-card-link ${!isList ? "member-card-link--card" : ""}`}
          onClick={() => applyDrillFilters({ userId: user.userId })}
          title="View All Tasks"
        >
          <Iconify icon="carbon:view" />
          <span className="member-card-link-text">{isList ? "View" : "View All Tasks"}</span>
        </button>
      </div>
    );
  };

  if (!teamId) {
    return (
      <div className="team-overview-section center-top-space">
        <NoDataFound message="Team not found. Open Team Overview from My Teams." />
      </div>
    );
  }

  return (
    <div className="team-overview-section center-top-space">
      <div className={`team-overview-filters-panel ${isLoading && hasLoaded ? "is-applying" : ""}`}>
        <div className="team-overview-filters">
          <div className="team-overview-header-main">
            <h2 className="team-overview-title">{teamName} Overview</h2>
            {headerMeta ? <div className="team-overview-subtitle">{headerMeta}</div> : null}
          </div>
          <OverviewFilterField
            label="Planning"
            icon="mdi:calendar-month-outline"
            value={filters.planning}
            options={filterOptions.planningCycles}
            onChange={(opt) => handleFilterChange("planning", opt)}
            placeholder="Select planning"
          />
          <OverviewFilterField
            label="Project"
            icon="mdi:sitemap-outline"
            value={filters.project}
            options={filterOptions.projects}
            onChange={(opt) => handleFilterChange("project", opt)}
            placeholder="All Projects"
          />
          <OverviewFilterField
            label="Team Member"
            icon="mdi:account-plus-outline"
            value={filters.member}
            options={filterOptions.members}
            onChange={(opt) => handleFilterChange("member", opt)}
            placeholder="All Members"
          />
          <OverviewFilterField
            label="Task Type"
            icon="mdi:shape-outline"
            value={filters.taskCategory}
            options={filterOptions.taskCategories}
            onChange={(opt) => handleFilterChange("taskCategory", opt)}
            placeholder="All Types"
          />
          <OverviewDateRangeField
            fromDate={filters.fromDate}
            toDate={filters.toDate}
            onChangeFrom={(date) => handleFilterChange("fromDate", date)}
            onChangeTo={(date) => handleFilterChange("toDate", date)}
          />

          <div className="filter-actions">
            <Buttons
              buttonText="Apply Filters"
              textWithIcon={false}
              buttonTypeClassName="theme-btn"
              className="overview-filter-apply-btn"
              onClick={handleApply}
              isLoading={isLoading}
              titleText="Apply Filters"
            />
            <Buttons
              buttonText="Clear"
              textWithIcon={false}
              buttonTypeClassName="dark-btn"
              className="overview-filter-clear-btn"
              onClick={handleReset}
              isDisable={isLoading}
              titleText="Clear Filters"
            />
          </div>
        </div>

        {activeChips.length > 0 ? (
          <div className="team-overview-chips">
            {activeChips.map((chip) => (
              <button
                type="button"
                key={chip.key}
                className="team-overview-chip"
                onClick={() => clearChip(chip.key)}
              >
                <span>{chip.label}</span>
                <Iconify icon="mdi:close" />
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {isLoading && !hasLoaded ? (
        <DataLoader />
      ) : loadError || isError ? (
        <NoDataFound message={loadError || "Unable to load Team Overview."} />
      ) : !dashboard ? (
        <NoDataFound message="No overview data for this team." />
      ) : (
        <>
          <div className="team-overview-kpi-grid">
            {KPI_META.map((meta) => (
              <div key={meta.key} className="team-overview-kpi-card">
                <div className="kpi-icon">
                  <Iconify icon={meta.icon} />
                </div>
                <div className="kpi-content">
                  <div className="kpi-label">{meta.label}</div>
                  <div className="kpi-value">{renderKpiValue(meta)}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="row team-overview-gap">
            <div className="col-12 col-lg-6">
              <CardSection cardTitle="Team Workload & Capacity">
                <div className="workload-card">
                  <div className="workload-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Team Member</th>
                          <th>Planned</th>
                          <th>Productive</th>
                          <th>Indirect</th>
                          <th>Total Cap.</th>
                          <th>Utilization</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workload.length === 0 ? (
                          <tr>
                            <td colSpan={7}>No members found</td>
                          </tr>
                        ) : (
                          workload.map((row) => {
                            const displayStatus = getDisplayUtilizationStatus(
                              row.utilizationStatus,
                              row.plannedHours
                            );
                            const util = Number(row.utilization || 0);
                            const productiveHours =
                              row.productiveHours != null && Number(row.productiveHours) > 0
                                ? Number(row.productiveHours)
                                : Number(row.availableHours || 0);
                            const indirectHours = Number(row.indirectHours || 0);
                            return (
                              <tr
                                key={row.userId}
                                className={
                                  OVERVIEW_NAVIGATION_ENABLED.workloadMember
                                    ? "workload-row-clickable"
                                    : undefined
                                }
                                onClick={
                                  OVERVIEW_NAVIGATION_ENABLED.workloadMember
                                    ? () => applyDrillFilters({ userId: row.userId })
                                    : undefined
                                }
                              >
                                <td>
                                  <div className="user-name">
                                    <Avatar alt={row.userName} size="small" shape="circle" />
                                    {row.userName}
                                  </div>
                                </td>
                                <td>{formatHours(row.plannedHours)}</td>
                                <td>{formatHours(productiveHours)}</td>
                                <td>{formatHours(indirectHours)}</td>
                                <td>{formatHours(row.availableHours)}</td>
                                <td>
                                  <div className="util-cell">
                                    <span>{util.toFixed(0)}%</span>
                                    <div className="util-progress">
                                      <div
                                        className="util-progress-fill"
                                        style={{ width: `${clampPercent(util)}%` }}
                                      />
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <span
                                    className={`util-badge ${getUtilizationStatusClass(displayStatus)}`}
                                  >
                                    {displayStatus}
                                  </span>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="workload-footnote">
                    Utilization uses Productive hours (task capacity). Indirect hours are non-task
                    capacity (meetings, etc.) from Resource Planning.
                  </div>
                </div>
              </CardSection>
            </div>

            <div className="col-12 col-lg-6">
              <div className="team-overview-charts-stack">
                <CardSection cardTitle="Tasks by Project (Status)">
                  <div className="team-overview-chart team-overview-chart--vertical">
                    {chartData.length > 0 ? (
                      <RenderChart
                        data={chartData}
                        chartType="bar"
                        height={Math.max(240, 220 + Math.min(chartData.length, 6) * 8)}
                        legendPosition="bottom"
                        showLegend
                        taskColors={taskColors}
                        tooltipMode="nearest"
                        tooltipIntersect={true}
                        onBarClick={
                          OVERVIEW_NAVIGATION_ENABLED.projectStatusChart
                            ? ({ projectLabel }) => {
                              const row = projectStatusRows.find(
                                (item) => item.projectName === projectLabel
                              );
                              if (row?.projectId) {
                                applyDrillFilters({ projectId: row.projectId });
                              }
                            }
                            : undefined
                        }
                      />
                    ) : (
                      <NoDataFound message="No project status data for the selected filters." />
                    )}
                  </div>
                </CardSection>

                <CardSection cardTitle="Task Status Distribution">
                  <div className="team-overview-pie-wrap">
                    {statusDistribution.length > 0 ? (
                      <>
                        <div className="team-overview-pie">
                          <RenderChart
                            data={statusDistribution.filter((s) => (s.data || 0) > 0)}
                            chartType="pie"
                            height={220}
                            width={220}
                            showLegend={false}
                            tooltipMode="nearest"
                            tooltipIntersect={true}
                          />
                        </div>
                        <ul className="pie-legend">
                          {statusDistribution.map((item) => {
                            const pct =
                              totalStatusTasks > 0
                                ? Math.round((item.data / totalStatusTasks) * 100)
                                : 0;
                            return (
                              <li key={item.label}>
                                <span
                                  className="pie-legend-dot"
                                  style={{ backgroundColor: item.color }}
                                />
                                <span className="pie-legend-label">{item.label}</span>
                                <span className="pie-legend-meta">
                                  {item.data} ({pct}%)
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </>
                    ) : (
                      <NoDataFound message="No status distribution for the selected filters." />
                    )}
                  </div>
                </CardSection>
              </div>
            </div>
          </div>

          <div className="team-overview-members-header">
            <div className="team-overview-members-title">Team Members</div>
            <div className="team-overview-members-toolbar">
              <div className="member-sort">
                <span className="member-sort-label">Sort by:</span>
                <div className="member-sort-select">
                  <DropdownSelect
                    value={memberSort}
                    optionsValue={MEMBER_SORT_OPTIONS}
                    placeholder="Sort by"
                    handleDropdownChange={setMemberSort}
                  />
                </div>
              </div>
              <div className={`member-view-toggle ${!canUseListView ? "grid-only" : ""}`}>
                <button
                  type="button"
                  className={memberView === "grid" ? "active" : ""}
                  onClick={() => setMemberView("grid")}
                  aria-label="Grid view"
                >
                  <Iconify icon="mdi:view-grid-outline" />
                </button>
                {canUseListView ? (
                  <button
                    type="button"
                    className={memberView === "list" ? "active" : ""}
                    onClick={() => setMemberView("list")}
                    aria-label="List view"
                  >
                    <Iconify icon="mdi:view-list-outline" />
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {canUseListView && memberView === "list" ? (
            <div className="member-list-panel">
              {sortedMembers.length > 0 ? (
                <div className="member-list-header" aria-hidden="true">
                  <div>Team Member</div>
                  <div>Utilization</div>
                  <div>Planned / Capacity</div>
                  <div>Productive / Indirect</div>
                  <div>Task Summary</div>
                  <div>Projects</div>
                  <div>Actions</div>
                </div>
              ) : null}

              <div className="member-list-rows">
                {sortedMembers.length === 0 ? (
                  <NoDataFound message="No team members found." />
                ) : (
                  sortedMembers.map((user) => renderMemberCard(user, "list"))
                )}
              </div>
            </div>
          ) : (
            <div className="row team-overview-gap member-view-grid">
              {sortedMembers.length === 0 ? (
                <div className="col-12">
                  <NoDataFound message="No team members found." />
                </div>
              ) : (
                sortedMembers.map((user) => (
                  <div className="col-12 col-md-6 col-xl-4" key={user.userId}>
                    {renderMemberCard(user, "grid")}
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TeamOverview;
